import {useCallback, useEffect, useState} from "react";

/**
 * Hält einen einzelnen Filterwert im Browser-URL-Query fest, ohne
 * Seitenwechsel (history.replaceState statt Inertia-Navigation oder
 * window.location) - Filterzustand bleibt beim Neuladen/Teilen des Links
 * erhalten, ohne dass eine Aktion die Seite neu lädt. Mehrere unabhängige
 * Aufrufe dieses Hooks auf einer Seite (je Filter einer) sind unproblematisch,
 * weil jeder Aufruf die aktuelle Query beim Schreiben frisch von
 * window.location liest statt eine veraltete Kopie zu überschreiben.
 */
export function useQueryState(key, initialValue) {
    const [value, setValue] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.has(key) ? params.get(key) : initialValue;
    });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (value === undefined || value === null || value === "") {
            params.delete(key);
        } else {
            params.set(key, value);
        }
        const query = params.toString();
        const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
        window.history.replaceState(null, "", url);
    }, [key, value]);

    return [value, setValue];
}

/** Wie useQueryState, aber für Mehrfachauswahlen (kommagetrennt in der URL). */
export function useQueryListState(key, initialValue = []) {
    const [raw, setRaw] = useQueryState(key, initialValue.length ? initialValue.join(",") : undefined);
    const list = raw ? String(raw).split(",").filter(Boolean) : [];
    const setList = useCallback(
        (nextList) => setRaw(Array.isArray(nextList) && nextList.length ? nextList.join(",") : undefined),
        [setRaw]
    );
    return [list, setList];
}

/** Wie useQueryState, aber als Schalter (0/1 statt "false"/"true" in der URL). */
export function useQueryBoolState(key, initialValue = false) {
    const [raw, setRaw] = useQueryState(key, initialValue ? "1" : undefined);
    const value = raw === "1";
    const setValueBool = useCallback((next) => setRaw(next ? "1" : undefined), [setRaw]);
    return [value, setValueBool];
}
